import { concat, difference, filter, find, flattenDeep, forEach, includes, isBoolean, isEmpty, isNil, map, remove, uniq } from 'lodash'
import papa from 'papaparse'

import FileColumnConfig, { COLUMN_TYPE_ALPHA, COLUMN_TYPE_ALPHA_NUMERIC, COLUMN_TYPE_DATE, COLUMN_TYPE_EMAIL, COLUMN_TYPE_NUMERIC, COLUMN_TYPE_WILDCARD } from '../models/FileColumnConfig'
import { TYPE as AGENT_TYPE, VALIDATION_RULES as AGENT_VALIDATION_RULES, PROPERTY as AGENT_PROPERTY } from '../models/dto/Agent'
import { TYPE as CLIENT_TYPE, VALIDATION_RULES as CLIENT_VALIDATION_RULES } from '../models/dto/Client'
import { TYPE as MORTGAGE_DEAL_TYPE, VALIDATION_RULES as MORTGAGE_DEAL_VALIDATION_RULES } from '../models/dto/MortgageDeal'
import { TYPE as OPERATION_TYPE, TRANSFER_MORTGAGE_DEAL_AGENT_COLUMN_CONFIG, TRANSFER_MORTGAGE_DEAL_AGENT_PROFILE_COLUMN_CONFIG } from '../models/dto/Operation'
import { VALID_TABLES, ACTIONS, RESULT, } from '../constants'
import { duplicates } from './utility.service'
import { CSV_ACTIONS } from './api/csv.service'

const VALIDATION_RULES = {
  ...AGENT_VALIDATION_RULES,
  ...CLIENT_VALIDATION_RULES,
  ...MORTGAGE_DEAL_VALIDATION_RULES,
}

// Primary keys required by other columns.
const agentPK = ['agent_id',]
const agentProfilePK = ['agent_id', 'profile_id',]
const clientPK = ['client_id',]
const coappPK = ['coapp_id',]
const mortgagePK = ['mortgage_id',]
const mortgageDealPK = ['mortgage_deal_id',]
const mortgageDealAssociationPK = ['agent_id', 'profile_id', 'mortgage_deal_id',]
const morgageDealCommentPK = ['mortgage_deal_comment_id', 'mortgage_deal_id',]

// Columns needed to add or update an Agent entity via CSV file.
const AGENT_COLUMN_CONFIG = [
  {
    alias: 'Agent',
    columns: [
      (new FileColumnConfig('active', 'Active Status', COLUMN_TYPE_ALPHA)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT),
      (new FileColumnConfig('agent_id', 'Agent ID', COLUMN_TYPE_NUMERIC)).requires(agentPK).disable().useValidRules(AGENT_TYPE.AGENT),
      (new FileColumnConfig('boss_broker_code', 'BOSS Broker Code', COLUMN_TYPE_NUMERIC)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT),
      (new FileColumnConfig('brokerage_number', 'Brokerage Number', COLUMN_TYPE_NUMERIC)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT),
      (new FileColumnConfig('designation', 'Designation', COLUMN_TYPE_WILDCARD)).requires(agentPK).optional().useValidRules(AGENT_TYPE.AGENT),
      (new FileColumnConfig('first_name', 'First Name', COLUMN_TYPE_WILDCARD)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT),
      (new FileColumnConfig('legacy_agent_id', 'Legacy Agent ID', COLUMN_TYPE_WILDCARD)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT),
      (new FileColumnConfig('surname', 'Surname', COLUMN_TYPE_WILDCARD)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT),
      (new FileColumnConfig('vendor_id', 'Vendor ID', COLUMN_TYPE_ALPHA_NUMERIC)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT),
    ],
  },
  {
    alias: 'Agent Profile',
    columns: [
      (new FileColumnConfig('profile_id', 'Agent Profile', COLUMN_TYPE_ALPHA)).requires(agentPK).disable().useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('application_url', 'Application URL', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).optional().useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('email_address', 'Email Address', COLUMN_TYPE_EMAIL)).requires(agentProfilePK).useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('mobile_number', 'Mobile Number', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).optional().useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('phone_extension', 'Phone Extension', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).optional().useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('phone_number', 'Phone Number', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('plural', 'Plural', COLUMN_TYPE_ALPHA)).requires(agentProfilePK).useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('profile_photo_file', 'Profile Photo File', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).optional().useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('provincial_disclosure', 'Provincial Disclosure', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).optional().useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('signature_photo_file', 'Signature Photo File', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).optional().useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('brand_logo_file', 'Brand Logo File', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).optional().useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('title', 'Title', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).optional().useValidRules(AGENT_TYPE.AGENT_PROFILE),
      (new FileColumnConfig('website', 'Website URL', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).optional().useValidRules(AGENT_TYPE.AGENT_PROFILE),
    ],
  },
  {
    alias: 'Agent Alias',
    columns: [
      (new FileColumnConfig(AGENT_PROPERTY.AGENT_NAME_ALIAS, 'Agent Name Alias', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).useValidRules(AGENT_TYPE.AGENT_ALIAS).optional(),
      (new FileColumnConfig(AGENT_PROPERTY.EMAIL_SENDER_ALIAS, 'Email Sender Alias', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).useValidRules(AGENT_TYPE.AGENT_ALIAS).optional(),
      (new FileColumnConfig(AGENT_PROPERTY.EMAIL_SIGNOFF_NAME_ALIAS, 'Email Sign-off Alias', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).useValidRules(AGENT_TYPE.AGENT_ALIAS).optional(),
      (new FileColumnConfig(AGENT_PROPERTY.PHONE_NUMBER_ALIAS, 'Phone Number Alias', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).useValidRules(AGENT_TYPE.AGENT_ALIAS).optional(),
      (new FileColumnConfig(AGENT_PROPERTY.MOBILE_NUMBER_ALIAS, 'Mobile Number Alias', COLUMN_TYPE_WILDCARD)).requires(agentProfilePK).useValidRules(AGENT_TYPE.AGENT_ALIAS).optional(),
    ],
  },
  {
    // In this case, we're updating the "Agent Team Association".
    // The actual "Agent Team" teams are updated via valid table.
    alias: 'Agent Team',
    columns: [
      (new FileColumnConfig('team_name', 'Team', COLUMN_TYPE_WILDCARD)).requires(agentPK).useValidRules(VALID_TABLES.AGENT_TEAM),
    ],
  },
  {
    alias: 'Business Address',
    columns: [
      (new FileColumnConfig('address', 'Address', COLUMN_TYPE_WILDCARD)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT_BUSINESS_ADDRESS),
      (new FileColumnConfig('city', 'City', COLUMN_TYPE_WILDCARD)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT_BUSINESS_ADDRESS),
      (new FileColumnConfig('postal_code', 'Postal Code', COLUMN_TYPE_ALPHA_NUMERIC)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT_BUSINESS_ADDRESS),
      (new FileColumnConfig('province', 'Province', COLUMN_TYPE_ALPHA)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT_BUSINESS_ADDRESS),
      (new FileColumnConfig('valid_address', 'Valid Address', COLUMN_TYPE_ALPHA)).requires(agentPK).useValidRules(AGENT_TYPE.AGENT_BUSINESS_ADDRESS),
    ],
  },
  {
    alias: 'Social Media',
    columns: [
      (new FileColumnConfig('facebook', 'Facebook URL', COLUMN_TYPE_WILDCARD)).requires(agentPK).optional().useValidRules(AGENT_TYPE.AGENT_SOCIAL_MEDIA, 'url'),
      (new FileColumnConfig('google_my_business', 'Google My Business URL', COLUMN_TYPE_WILDCARD)).requires(agentPK).optional().useValidRules(AGENT_TYPE.AGENT_SOCIAL_MEDIA, 'url'),
      (new FileColumnConfig('instagram', 'Instagram URL', COLUMN_TYPE_WILDCARD)).requires(agentPK).optional().useValidRules(AGENT_TYPE.AGENT_SOCIAL_MEDIA, 'url'),
      (new FileColumnConfig('linkedin', 'LinkedIn URL', COLUMN_TYPE_WILDCARD)).requires(agentPK).optional().useValidRules(AGENT_TYPE.AGENT_SOCIAL_MEDIA, 'url'),
      (new FileColumnConfig('twitter', 'Twitter URL', COLUMN_TYPE_WILDCARD)).requires(agentPK).optional().useValidRules(AGENT_TYPE.AGENT_SOCIAL_MEDIA, 'url'),
      (new FileColumnConfig('youtube', 'Youtube URL', COLUMN_TYPE_WILDCARD)).requires(agentPK).optional().useValidRules(AGENT_TYPE.AGENT_SOCIAL_MEDIA, 'url'),
    ],
  },
]

// Columns needed to add or update a Mortgage Deal entity via CSV file.
const MORTGAGE_COLUMN_CONFIG = [
  {
    alias: 'Mortgage',
    columns: [
      (new FileColumnConfig('mortgage_id', 'Mortgage ID', COLUMN_TYPE_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE).disable().mode(ACTIONS.UPDATE),
      (new FileColumnConfig('closing_date', 'Closing Date', COLUMN_TYPE_DATE)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE).optional(),
      (new FileColumnConfig('loan_code', 'Loan Code', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE).optional(),
      (new FileColumnConfig('maturity_date', 'Maturity Date', COLUMN_TYPE_DATE)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE).optional(),
    ],
  },
  {
    alias: 'Mortgage Property',
    columns: [
      (new FileColumnConfig('mortgage_property_address', 'Address (Mortgage Property)', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_PROPERTY),
      (new FileColumnConfig('mortgage_property_city', 'City (Mortgage Property)', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_PROPERTY),
      (new FileColumnConfig('mortgage_property_postal_code', 'Postal Code (Mortgage Property)', COLUMN_TYPE_ALPHA_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_PROPERTY),
      (new FileColumnConfig('mortgage_property_province', 'Province (Mortgage Property)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_PROPERTY),
    ],
  },
  {
    alias: 'Mortgage Deal',
    columns: [
      (new FileColumnConfig('mortgage_deal_id', 'Mortgage Deal ID', COLUMN_TYPE_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL).disable().mode(ACTIONS.UPDATE),
      (new FileColumnConfig('mortgage_id', 'Mortgage ID', COLUMN_TYPE_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL).disable().mode(ACTIONS.UPDATE),
      (new FileColumnConfig('mortgage_deal_date_created', 'Date Created', COLUMN_TYPE_DATE)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL),
      (new FileColumnConfig('mortgage_deal_date_paid', 'Date Paid', COLUMN_TYPE_DATE)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL).optional(),
      (new FileColumnConfig('mortgage_deal_fee', 'Fee', COLUMN_TYPE_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL),
      (new FileColumnConfig('mortgage_deal_language', 'Language', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL),
      (new FileColumnConfig('mortgage_deal_program', 'Program', COLUMN_TYPE_ALPHA_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL),
      (new FileColumnConfig('mortgage_deal_referral_company', 'Referral Company', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL).optional(),
      (new FileColumnConfig('mortgage_deal_status', 'Status', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL),
      (new FileColumnConfig('mortgage_deal_term_end_date', 'Term End Date', COLUMN_TYPE_DATE)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL),
      (new FileColumnConfig('mortgage_deal_term_length_years', 'Term Length', COLUMN_TYPE_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL),
      (new FileColumnConfig('mortgage_deal_term_start_date', 'Term Start Date', COLUMN_TYPE_DATE)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL),
    ],
  },
  {
    alias: 'Mortgage Deal Program Control',
    columns: [
      (new FileColumnConfig('mortgage_deal_id', 'Mortgage Deal ID', COLUMN_TYPE_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL).disable(),
      (new FileColumnConfig('mortgage_deal_batch', 'Batch (Mortgage Deal)', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_resend_tyc', 'Resend TYC (Mortgage Deal)', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL).optional(),
      (new FileColumnConfig('mortgage_deal_return_ba', 'Return BA (Mortgage Deal)', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL).optional(),
      (new FileColumnConfig('mortgage_deal_return_tyc', 'Return TYC (Mortgage Deal)', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL).optional(),
      (new FileColumnConfig('mortgage_deal_return_ml', 'Return ML (Mortgage Deal)', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL).optional(),
      (new FileColumnConfig('mortgage_deal_return_cal', 'Return CAL (Mortgage Deal)', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL).optional(),
      (new FileColumnConfig('mortgage_deal_sent_tyc', 'Sent TYC (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_sent_ml', 'Sent ML (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_suppress_tyc', 'Suppress TYC (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_suppress_ml', 'Suppress ML (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_suppress_ba', 'Suppress BA (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_suppress_mag', 'Suppress MAG (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_suppress_cal', 'Suppress CAL (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_suppress_bd', 'Suppress BD (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_include_calendar', 'Include Calendar (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_hold', 'Hold (Mortgage Deal)', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL),
      (new FileColumnConfig('mortgage_deal_message', 'Message (Mortgage Deal)', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_PROGRAM_CONTROL).optional(),
    ],
  },
  // Bulk updating associations will have to be handled in a different BLM.
  {
    alias: 'Agent',
    columns: [
      (new FileColumnConfig('agent_id', 'Agent ID', COLUMN_TYPE_NUMERIC)).requires(mortgageDealAssociationPK).useValidRules(AGENT_TYPE.AGENT).disable().mode(ACTIONS.ADD),
    ],
  },
  {
    alias: 'Agent Profile',
    columns: [
      (new FileColumnConfig('profile_id', 'Agent Profile', COLUMN_TYPE_ALPHA)).requires(mortgageDealAssociationPK).useValidRules(AGENT_TYPE.AGENT_PROFILE).disable().mode(ACTIONS.ADD),
    ],
  },
  {
    alias: 'Client',
    columns: [
      (new FileColumnConfig('client_id', 'Client ID', COLUMN_TYPE_NUMERIC)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT).disable().mode(ACTIONS.UPDATE),
      (new FileColumnConfig('client_date_of_birth', 'Date of Birth (Client)', COLUMN_TYPE_DATE)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT).optional(),
      (new FileColumnConfig('client_first_name', 'First Name (Client)', COLUMN_TYPE_WILDCARD)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT),
      (new FileColumnConfig('client_is_co_applicant', 'Is Co-Applicant? (Client)', COLUMN_TYPE_ALPHA)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT).disable().mode(ACTIONS.UPDATE),
      (new FileColumnConfig('client_surname', 'Surname (Client)', COLUMN_TYPE_WILDCARD)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT),
    ],
  },
  {
    alias: 'Client Address',
    columns: [
      (new FileColumnConfig('client_address', 'Address (Client)', COLUMN_TYPE_WILDCARD)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT_ADDRESS),
      (new FileColumnConfig('client_city', 'City (Client)', COLUMN_TYPE_WILDCARD)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT_ADDRESS),
      (new FileColumnConfig('client_postal_code', 'Postal Code (Client)', COLUMN_TYPE_ALPHA_NUMERIC)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT_ADDRESS),
      (new FileColumnConfig('client_province', 'Province (Client)', COLUMN_TYPE_ALPHA)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT_ADDRESS),
      (new FileColumnConfig('client_valid_address', 'Valid Address? (Client)', COLUMN_TYPE_ALPHA)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT_ADDRESS),
    ],
  },
  {
    alias: 'Client Contact',
    columns: [
      (new FileColumnConfig('client_email_address', 'Email Address (Client)', COLUMN_TYPE_EMAIL)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT_CONTACT).optional(),
    ],
  },
  {
    alias: 'Client Program Control',
    columns: [
      (new FileColumnConfig('client_suppress_email', 'Suppress Email? (Client)', COLUMN_TYPE_ALPHA)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT_PROGRAM_CONTROL).optional().ignoreFileValidation(),
      (new FileColumnConfig('client_bounce', 'Bounce (Client)', COLUMN_TYPE_WILDCARD)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT_PROGRAM_CONTROL).optional(),
      (new FileColumnConfig('client_unsubscribe', 'Unsubscribe (Client)', COLUMN_TYPE_WILDCARD)).requires(clientPK).useValidRules(CLIENT_TYPE.CLIENT_PROGRAM_CONTROL).optional(),
    ],
  },
  {
    alias: 'Co-Applicant',
    columns: [
      (new FileColumnConfig('coapp_id', 'Co-Applicant ID', COLUMN_TYPE_NUMERIC)).requires(coappPK).useValidRules(CLIENT_TYPE.CO_APP).disable().mode(ACTIONS.UPDATE),
      (new FileColumnConfig('coapp_date_of_birth', 'Date of Birth (Co-Applicant)', COLUMN_TYPE_DATE)).requires(coappPK).useValidRules(CLIENT_TYPE.CO_APP).optional(),
      (new FileColumnConfig('coapp_first_name', 'First Name (Co-Applicant)', COLUMN_TYPE_WILDCARD)).requires(coappPK).useValidRules(CLIENT_TYPE.CO_APP),
      (new FileColumnConfig('coapp_is_co_applicant', 'Is Co-Applicant? (Co-Applicant)', COLUMN_TYPE_ALPHA)).requires(coappPK).useValidRules(CLIENT_TYPE.CO_APP).disable().mode(ACTIONS.UPDATE),
      (new FileColumnConfig('coapp_surname', 'Surname (Co-Applicant)', COLUMN_TYPE_WILDCARD)).requires(coappPK).useValidRules(CLIENT_TYPE.CO_APP),
    ],
  },
  {
    alias: 'Co-Applicant Contact',
    columns: [
      (new FileColumnConfig('coapp_email_address', 'Email Address (Co-Applicant)', COLUMN_TYPE_EMAIL)).requires(coappPK).useValidRules(CLIENT_TYPE.CO_APP_CONTACT).optional(),
    ],
  },
  {
    alias: 'Co-Applicant Program Control',
    columns: [
      (new FileColumnConfig('coapp_suppress_email', 'Suppress Email? (Co-Applicant)', COLUMN_TYPE_ALPHA)).requires(coappPK).useValidRules(CLIENT_TYPE.CO_APP_PROGRAM_CONTROL).optional().ignoreFileValidation(),
      (new FileColumnConfig('coapp_bounce', 'Bounce (Co-Applicant)', COLUMN_TYPE_WILDCARD)).requires(coappPK).useValidRules(CLIENT_TYPE.CO_APP_PROGRAM_CONTROL).optional(),
      (new FileColumnConfig('coapp_unsubscribe', 'Unsubscribe (Co-Applicant)', COLUMN_TYPE_WILDCARD)).requires(coappPK).useValidRules(CLIENT_TYPE.CO_APP_PROGRAM_CONTROL).optional(),
    ],
  },
]

// Columns needed to add or update a Mortgage Deal Comment entity via CSV file.
const MORTGAGE_COMMENT_COLUMN_CONFIG = [
  {
    alias: 'Mortgage Deal',
    columns: [
      (new FileColumnConfig('mortgage_deal_id', 'Mortgage Deal ID', COLUMN_TYPE_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL).disable(),
    ],
  },
  {
    alias: 'Mortgage Deal Comment',
    columns: [
      // (new FileColumnConfig('mortgage_deal_comment_id', 'Mortgage Deal Comment ID', COLUMN_TYPE_NUMERIC)).requires(morgageDealCommentPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_COMMENT).disable(),
      (new FileColumnConfig('mortgage_deal_id', 'Mortgage Deal ID', COLUMN_TYPE_NUMERIC)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_COMMENT).disable(),
      (new FileColumnConfig('comment_type', 'Mortgage Deal Comment Type', COLUMN_TYPE_ALPHA)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_COMMENT),
      (new FileColumnConfig('comment', 'Mortgage Deal Comment', COLUMN_TYPE_WILDCARD)).requires(mortgageDealPK).useValidRules(MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_COMMENT),
    ],
  },
]

// Column-indicator constants.
const INDICATOR_REQUIRED_PATTERN = '**'

// Co-app required columns must all contain a value or be empty.
const COAPP_REQUIRED_RULE = ['coapp_first_name', 'coapp_surname', 'coapp_unsubscribed', 'coapp_is_co_applicant', 'coapp_id',]
const INDICATOR_COAPP_REQUIRED_PATTERN = '^^'

// Co-app optional columns must be empty unless the COAPP_REQUIRED_RULE is satisified.
const COAPP_OPTIONAL_RULE = ['coapp_date_of_birth', 'coapp_email_address',]


export default class CSVFileService {
  /**
   * Create a CSVFileService object for a given data type and mode.
   * Contains the model of the file and logic necessary to generate and validate the file.
   * @param {string} dataType Type of data contained in the CSV file.
   * @param {string} mode Mode the CSV is being created for.
   */
  constructor(dataType, mode) {
    // What mode is this CSV file being generated in?
    if (includes(CSV_ACTIONS, mode)) {
      this.mode = mode
    }
    else {
      throw new Error(`"${type}" is an unknown mode`)
    }

    // Columns assigned by the constructor based on the data type.
    switch (dataType) { 
      case AGENT_TYPE.AGENT:
        this.columns = AGENT_COLUMN_CONFIG
        break
      case MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL:
        this.columns = MORTGAGE_COLUMN_CONFIG
        break
      case MORTGAGE_DEAL_TYPE.MORTGAGE_DEAL_COMMENT:
        this.columns = MORTGAGE_COMMENT_COLUMN_CONFIG
        break
      case OPERATION_TYPE.TRANSFER_MORTGAGE_DEAL_AGENT:
        this.columns = TRANSFER_MORTGAGE_DEAL_AGENT_COLUMN_CONFIG
        break
      case OPERATION_TYPE.TRANSFER_MORTGAGE_DEAL_AGENT_PROFILE:
        this.columns = TRANSFER_MORTGAGE_DEAL_AGENT_PROFILE_COLUMN_CONFIG
        break
      default:
        throw new Error(`"${dataType}" is an unknown data type`)
    }

    // Create a flat list of all columns for this data type.
    this.columnList = []
    forEach(this.columns, columnGroup => {
      this.columnList = [...this.columnList, ...columnGroup.columns,]
    })
  }

  /**
   * Get an array of the column metadata for this data type. The data is formatted to be displayed
   * in a v-combobox element.
   * @returns An array of the columns for this data type.
   */
  getColumns() {
    let formattedColumns = []

    // Add each column category as a v-combobox list header for its child columns.
    forEach(this.columns, columnGroup => {
      // Only add a column group if it has visible columns.
      if (columnGroup.columns.length >= 1) {
        formattedColumns.push({ header: columnGroup.alias, })
        forEach(columnGroup.columns, column => {
          if (column.enabled) {
            formattedColumns.push(column)
          }
        })
      }
    })

    return formattedColumns;
  }

  /**
   * Get a set of headers expected to be in this file for the current file mode.
   * @param {Array<String>} headers Headers for which to get expected headers.
   * @returns A set of keys with indicator-characters that are expected to be in the file.
   */
  getExpectedHeaders(headers) {
    let expectedHeaders = null

    switch (this.mode) { 
      case ACTIONS.ADD:
        // Return all headers for this data type that are permitted to be initialized.
        // There are some cases where an ID for an object is auto-generated in the database,
        // and it is only permitted to include the column header on update.
        expectedHeaders = []
        forEach(this.getHeaders(), header => {
          let fileColumnConfig = find(this.columnList, { name: header, })
          if (fileColumnConfig.isAddColumn()) {
            expectedHeaders.push(header)
          }
        })
        break

      case ACTIONS.UPDATE:
        // Get the header-dependencies for the passed-in headers. Merge the two into
        // a set and indicate which member headers are required.
        expectedHeaders = uniq(concat(headers, this.getRequiredHeaders(headers)))
        break
      
      case ACTIONS.OPERATION:
        expectedHeaders = this.getHeaderWithDescription()
        break
    }

    return this.setHeaderIndicator(expectedHeaders)
  }

  /**
   * Generate a CSV file template based on the data type and mode. When adding data through CSV all
   * of the columns for the data type will be used. When updating data through CSV, only the
   * selected columns (and any dependencies those columns have) will be used.
   * @param {Array<Object>} columns Array of column metadata objects used to specify which columns to use.
   * @returns The header row of the CSV file as a string.
   */
  getFileTemplate(columns) {
    let headers = null

    switch (this.mode) { 
      case ACTIONS.OPERATION:
      case ACTIONS.ADD:
        if (columns) {
          throw new Error(`getFileTemplate(): columns detected while in "${this.mode}" mode`)
        }
        break

      case ACTIONS.UPDATE:
        if (!columns) {
          throw new Error(`getFileTemplate(): columns missing while in "${this.mode}" mode`)
        }
        // Extract the header list from the columns and get the expected headers for them.
        headers = map(columns, column => column.name)
        break
    }

    headers = this.getExpectedHeaders(headers)
    headers = [...new Set(headers),] // Ensure no duplicate columns.

    return `${headers.toString()}\n`
  }

  /**
   * Get an array of headers from metadata associated with the data type of the file.
   * @returns Array of header strings.
   */
  getHeaders() {
    return map(this.columnList, column => column.name)
  }

  getHeaderWithDescription() {
    return map(this.columnList, column => `${column.name} (${column.text})`)
  }

  /**
   * Get a set of headers that the incoming headers are dependant upon.
   * @param {Array<String>} headers Headers for which to get dependencies.
   * @returns Set of headers that are dependencies for the input headers.
   */
  getRequiredHeaders(headers) {
    let fileColumnConfig = null
    let requiredColumns = []

    forEach(headers, header => {
      fileColumnConfig = find(this.columnList, { name: header, })
      if (fileColumnConfig) {
        requiredColumns.push(fileColumnConfig._requires)
      }
    })

    return uniq(flattenDeep(requiredColumns))
  }

  /**
   * Normalize a header string by trimming white-space, removing indicator-characters, and
   * converting to lowercase.
   * @param {string} header Header to normalize.
   * @returns A normalized header string.
   */
  normalizeHeader(header) {
    return header
      .trim()
      .replace(INDICATOR_REQUIRED_PATTERN, '')
      .replace(INDICATOR_COAPP_REQUIRED_PATTERN, '')
      .toLowerCase();
  }

  /**
   * Normalize an array of header strings.
   * @param {Array<String>} headers Headers to normalize.
   * @returns Array of normalized header strings.
   */
  normalizeHeaders(headers) {
    return map(headers, header => this.normalizeHeader(header))
  }

  /**
   * Wrapper for calling papa.parse().
   * @param {*} file - File to parse.
   * @returns A promise from papa.parse().
   */
  parseCSV(file) {
    return new Promise((resolve, reject) => {
      return papa.parse(file, {
        quotes: false,
        quoteChar: '"',
        escapeChar: '\\',
        delimiter: ",",
        header: true,
        newline: "",
        skipEmptyLines: true,
        columns: null,
        complete: results => resolve(results),
        error: error => reject(error),
      })
    })
  }

  /**
   * Set an indicator character for a list of headers. Indicator characters are set based on the
   * conditional requirements of each indicator.
   * @param {Array<String>} headers Headers to decorate with an indicator character. 
   * @returns An array of headers with an indicator-character concatenated to headers that meet the
   * criteria of the indicator.
   */
  setHeaderIndicator(headers) {
    let fileColumnConfig = null
    let indicatedHeader = null

    headers = map(headers, header => {
      fileColumnConfig = find(this.columnList, { name: header, })

      // Add 'required' indicator.
      if (fileColumnConfig && fileColumnConfig.required) {
        if (COAPP_REQUIRED_RULE.includes(header)) {
          indicatedHeader = `${fileColumnConfig.name}${INDICATOR_COAPP_REQUIRED_PATTERN}`
        } else {
          indicatedHeader = `${fileColumnConfig.name}${INDICATOR_REQUIRED_PATTERN}`
        }
      }
      else {
        indicatedHeader = header
      }

      return indicatedHeader
    })

    return headers
  }

  /**
   * Validate the contents of every column in every row and append any errors related to data integrity.
   * @param {*} result PapaParse result object.
   */
  dataIntegrityCheck(result) {
    // Setup the object to receive the errors.
    // If there aren't any errors, this property will be deleted prior to returning.
    const ERROR_INVALID_ROW_DATA = 'invalid_row_data'
    this._validation.errors[ERROR_INVALID_ROW_DATA] = {
      message: 'File rows contain invalid data.',
      data: [],
    }

    let fileColumnConfig = null
    let message = null
    let rulesTarget = null
    let rules = null
    let row = null
    
    let isCoappRequiredColumn = false
    let coappOptionalColumnTotalLength = 0
    let coappRequiredColumnTotalLength = 0
    let newTotalLength = 0
    let coappRequiredAllEmpty = false

    // For each row in the file.
    forEach(result.data, (data, i) => {
      row = i + 2

      coappOptionalColumnTotalLength = 0
      coappRequiredColumnTotalLength = 0
      newTotalLength = 0
      coappRequiredAllEmpty = false

      // For each column in the row.
      forEach(data, (value, column) => {
        // Reset the message for each column.
        message = null

        // Get the config associated with this column.
        // If we're checking the column of an OPERATION, then we want to strip the extra text in parenthesis.
        let columnName = this.mode === ACTIONS.OPERATION ? column.split(" ", 1)[0] : this.normalizeHeader(column)
        fileColumnConfig = find(this.columnList, { name: columnName, })

        // There are some columns that we want the user to be able to leave null in the file, but we know have data validation
        // on the server side and database in the form of integrity constraints or default values. For these columns, we want
        // to skip validation altogether.
        if (fileColumnConfig.isIgnoringFileValidation()) {
          return
        }

        // Peform the checks for the co-applicant required/optional columns.
        isCoappRequiredColumn = COAPP_REQUIRED_RULE.includes(fileColumnConfig.name)
        if (isCoappRequiredColumn) {
          newTotalLength = coappRequiredColumnTotalLength + value.length

          if (newTotalLength === coappRequiredColumnTotalLength) {
            if (coappRequiredColumnTotalLength === 0) {
              // Assume that the required columns are empty to catch a later required column with a value.
              coappRequiredAllEmpty = true
            }
            else {
              // The case when some of the columns have values then an empty column is detected.
              this._validation.errors[ERROR_INVALID_ROW_DATA].data.push({ row, column, value: '', message: `Detected "${INDICATOR_COAPP_REQUIRED_PATTERN}" columns with values followed by this empty column. All required co-applicant columns must have a value or be empty.`, })
              return true
            }
          } else {
            if (coappRequiredAllEmpty) {
              // The case when some of the columns are empty then a non-empty column is detected.
              this._validation.errors[ERROR_INVALID_ROW_DATA].data.push({ row, column, value: '', message: `Detected empty "${INDICATOR_COAPP_REQUIRED_PATTERN}" columns followed by this column with a value. All required co-applicant columns must have a value or be empty.`, })
              return true
            }
          }
          coappRequiredColumnTotalLength = newTotalLength
        }
        else {
          // Check if this non-co-applicant column is missing required data.
          if (fileColumnConfig.required && value.length === 0) {
            this._validation.errors[ERROR_INVALID_ROW_DATA].data.push({ row, column, value, message: 'Value is required.', })
            return true
          }
        }

        // Record if there is data in this optional co-applicant column.
        if (COAPP_OPTIONAL_RULE.includes(fileColumnConfig.name)) {
          coappOptionalColumnTotalLength += value.length
        }

        // First-pass column-validation based on ColumnFileConfig.
        if (value.length > 0) {
          message = fileColumnConfig.validate(value)
          if (!isBoolean(message)) {
            this._validation.errors[ERROR_INVALID_ROW_DATA].data.push({ row, column, value, message, })
            return true
          }
        }

        // Second-pass column-validation based on the ValidationRules from the validation service.
        rulesTarget = fileColumnConfig.rulesTarget
        if (rulesTarget) {
          let errorFound = false

          try {
            rules = VALIDATION_RULES[rulesTarget.table]?.[rulesTarget.column].rules
          }
          catch (err) {
            if (err instanceof TypeError) {
              console.error("Validation lookup failed:", {
                table: rulesTarget?.table,
                column: rulesTarget?.column,
                message: err.message
              })
              message = `Validation rule not found for table "${rulesTarget.table}" and column "${rulesTarget.column}".`
              this._validation.errors[ERROR_INVALID_ROW_DATA].data.push({ row, column, value: '', message, })
            }
            else {
              console.error("Unexpected error:", err)
              message = err
              this._validation.errors[ERROR_INVALID_ROW_DATA].data.push({ row, column, value: '', message, })
            }
          }

          forEach(rules, rule => {
            // Don't report more than 1 error.
            if (errorFound) {
              return
            }

            message = rule(value)

            if (!isBoolean(message)) {
              if (value.length === 0 && message.includes('characters or fewer')) {
                message = null // Work-around for empty-strings tripping a length validation rule...
              }
              else if (isCoappRequiredColumn && message === 'Value is required.' || coappRequiredAllEmpty) {
                message = null // There is a check for this aleady above, redundant.
              }
              else if (
                this.mode === ACTIONS.ADD && value.length === 0 && !column.includes('**') &&
                (message === 'Value is required.' || message === 'Invalid format. Requires: remove the "www." prefix')
              ) {
                // This is to capture the case where:
                //   - We want social media rules to require a value in the application but we don't want them to require a value
                //     when we're bulk-adding agents that may not have social media values.
                //   - We want to force social media to contain values upon updating.
                message = null
              }
              else {
                if (value.length > 15) {
                  value = value.slice(0,15) + '...' // Truncate the value for display purposes.
                }

                this._validation.errors[ERROR_INVALID_ROW_DATA].data.push({ row, column, value, message, })

                errorFound = true

                return true
              }
            }
          })
        }
      })

      if (coappRequiredAllEmpty && coappOptionalColumnTotalLength > 0) {
        const columnList = `${COAPP_OPTIONAL_RULE.toString().replaceAll(",", ", ")}`
        message = 'All optional co-appliant columns must be empty when the required co-applicant columns are empty.'
        this._validation.errors[ERROR_INVALID_ROW_DATA].data.push({ row, column: columnList, value: '', message, })
      }
    })

    // Delete the property if there weren't any errors in the row data.
    if (this._validation.errors[ERROR_INVALID_ROW_DATA].data.length === 0) {
      delete this._validation.errors[ERROR_INVALID_ROW_DATA]
    }
  }

  /**
   * Append any errors related to duplicate file headers.
   * @param {Array<string>} headers Array of normalized headers.
   */
   duplicateHeaderCheck(headers) {
    let duplicateHeaders = duplicates(headers)

    if (!isEmpty(duplicateHeaders)) {
      this._validation.errors.duplicate_headers = {
        message: 'File contains duplicate column headers.',
        data: duplicateHeaders,
      }
    }
  }

  /**
   * Append any errors related to invalid file headers.
   * @param {Array<string>} headers Array of normalized headers.
   */
  invalidHeaderCheck(headers) {
    let modelHeaders = this.mode === ACTIONS.OPERATION ? this.getHeaderWithDescription() : this.getHeaders()

    let invalidHeaders = headers.map(header => {
      if (!modelHeaders.includes(header)) {
        return header
      }
    })

    invalidHeaders = remove(invalidHeaders, header => !isNil(header))

    if (invalidHeaders.length > 0) {
      this._validation.errors.invalid_headers = {
        message: 'File contains unexpected column headers.',
        data: invalidHeaders,
      }
    } 
  }

  /**
   * Append any errors related to missing data in the file.
   * @param {*} result PapaParse result object.
   */
   missingDataCheck(result) {
    if (result.data.length === 0) {
      this._validation.errors.invalid_length = {
        message: (result.meta.fields.length === 0) ? 'File is empty.' : 'File only contains one row of data.',
        resolution: 'At least two rows of data are needed: one containing column headers, and one containing the data.',
      }
    }
  }

  /**
   * Append any errors related to missing file headers.
   * @param {Array<string>} fileHeaders Raw header list from the CSV file.
   * @param {Array<string>} headers Array of normalized headers.
   */
  missingHeaderCheck(fileHeaders, headers) {
    let expectedHeaders = this.getExpectedHeaders(headers)
    let cleanedFileHeaders = map(fileHeaders, header => header.trim().toLowerCase())
    let headerDifferences = difference(expectedHeaders, cleanedFileHeaders)

    // Strip the null values from the array.
    let missingHeaders = filter(headerDifferences, header => {
      if (!isNil(header)) {
        return header
      }
    })

    if (!isEmpty(missingHeaders)) {
      this._validation.errors.missing_headers = {
        message: 'File is missing expected column headers.',
        data: missingHeaders,
      }
    }
  }

  /**
   * Cursory validation on the front-end for required columns and column data types.
   * @param {*} file File to be validated.
   * @returns Promise containing the results of the validation attempt from the parse and validation of the file.
   */
  validate(file) {
    // Setup the object to contain the validation attempt.
    this._validation = {
      errors: {},
      status: 'failed',
    }

    return new Promise((resolve, reject) => {
      this.parseCSV(file)
        .then(result => {
          this.missingDataCheck(result)

          let fileHeaders = result.meta.fields
          let headers = this.mode === ACTIONS.OPERATION ? fileHeaders : this.normalizeHeaders(fileHeaders)

          this.invalidHeaderCheck(headers)
          this.missingHeaderCheck(fileHeaders, headers)
          this.duplicateHeaderCheck(headers)

          // Let the user deal with any file/header errors before bothering with validating the data contained in the rows.
          if (!isEmpty(this._validation.errors)) {
            return resolve(this._validation)
          }

          this.dataIntegrityCheck(result)
          
          // Set the status based on whether there are errors or not.
          if (isEmpty(this._validation.errors)) {
            this._validation.status = RESULT.SUCCESS
          }

          return resolve(this._validation)
        })
        .catch(error => {
          return reject(error)
        })
    })
  }
}